import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtEur, fmtEur2 } from "@/lib/format";
import TimeReport from "@/pages/TimeReport";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Sparkles, TrendingUp, PiggyBank, Percent } from "lucide-react";
import { toast } from "sonner";

const CHART_COLORS = ["#4A947D", "#172532", "#78848D", "#D96A55", "#3E7D69"];

export default function Reports() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => setData((await api.get("/reports/dashboard")).data);
  useEffect(() => { load(); }, []);

  const gen = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reports/ai-insights");
      setInsights(data);
    } catch { toast.error("Errore AI, riprova"); }
    finally { setLoading(false); }
  };

  if (!data) return <div className="text-zen-ink2">Caricamento…</div>;

  const projRev = [...data.per_project].sort((a,b)=>b.revenue-a.revenue).slice(0, 6);

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <span className="zen-label">Analytics</span>
        <h1 className="mt-2 font-heading text-3xl">Reportistica</h1>
        <p className="text-zen-ink2 mt-1">Ricavi, ore fatturabili e marginalità per progetto.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="zen-card p-6" data-testid="report-kpi-revenue">
          <div className="flex items-start justify-between">
            <div>
              <div className="zen-label">Ricavi totali incassati</div>
              <div className="mt-2 font-heading text-3xl">{fmtEur(data.kpi.total_revenue)}</div>
              <div className="text-sm text-zen-ink2 mt-1">In attesa {fmtEur(data.kpi.pending_revenue)}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-zen-primary/10 text-zen-primary flex items-center justify-center"><PiggyBank size={18}/></div>
          </div>
        </div>
        <div className="zen-card p-6" data-testid="report-kpi-hours">
          <div className="flex items-start justify-between">
            <div>
              <div className="zen-label">Ore fatturabili</div>
              <div className="mt-2 font-heading text-3xl">{data.kpi.billable_hours.toFixed(1)}</div>
              <div className="text-sm text-zen-ink2 mt-1">{data.kpi.active_projects} progetti attivi</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-zen-info/15 text-zen-info flex items-center justify-center"><TrendingUp size={18}/></div>
          </div>
        </div>
        <div className="zen-card p-6" data-testid="report-kpi-margin">
          <div className="flex items-start justify-between">
            <div>
              <div className="zen-label">Marginalità media</div>
              <div className="mt-2 font-heading text-3xl">{data.kpi.avg_margin_pct}%</div>
              <div className="text-sm text-zen-ink2 mt-1">Ricavi al netto dei costi</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-zen-subtle/40 text-zen-ink flex items-center justify-center"><Percent size={18}/></div>
          </div>
        </div>
      </div>

      <div className="zen-card p-6" data-testid="report-projects-margin">
        <span className="zen-label">Progetti</span>
        <h2 className="font-heading text-xl mt-1 mb-6">Marginalità per progetto</h2>
        {projRev.length === 0 ? (
          <p className="text-zen-ink2 text-sm py-6 text-center">Nessun progetto con ricavi ancora.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={projRev} layout="vertical" margin={{left: 20, right: 20}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E3D8"/>
                <XAxis type="number" stroke="#78848D" fontSize={12} tickFormatter={(v)=>`€${v}`}/>
                <YAxis type="category" dataKey="name" stroke="#78848D" fontSize={12} width={140}/>
                <Tooltip formatter={(v, k) => k === "margin_pct" ? `${v}%` : fmtEur2(v)} contentStyle={{borderRadius:12, border:"1px solid #E5E3D8"}}/>
                <Bar dataKey="revenue" name="Ricavi" radius={[0,8,8,0]}>
                  {projRev.map((p, i) => <Cell key={p.project_id} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="zen-card p-6" data-testid="report-projects-table">
        <span className="zen-label">Dettaglio</span>
        <h2 className="font-heading text-xl mt-1 mb-4">Tabella progetti</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zen-ink2 border-b border-zen-border">
              <tr>
                <th className="text-left py-3 font-medium">Progetto</th>
                <th className="text-right py-3 font-medium">Ricavi</th>
                <th className="text-right py-3 font-medium">Costi</th>
                <th className="text-right py-3 font-medium">Margine</th>
                <th className="text-right py-3 font-medium">Ore</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zen-border">
              {data.per_project.map(p => (
                <tr key={p.project_id}>
                  <td className="py-3 font-medium text-zen-ink">{p.name}</td>
                  <td className="py-3 text-right">{fmtEur2(p.revenue)}</td>
                  <td className="py-3 text-right text-zen-ink2">{fmtEur2(p.costs)}</td>
                  <td className={`py-3 text-right font-medium ${p.margin_pct >= 50 ? "text-zen-primary" : p.margin_pct >= 20 ? "text-zen-ink" : "text-zen-alert"}`}>{p.margin_pct}%</td>
                  <td className="py-3 text-right text-zen-ink2">{p.hours_logged.toFixed(1)}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TimeReport />

      <div className="zen-card p-6" data-testid="ai-insights-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <span className="zen-label">Powered by Claude Sonnet 4.5</span>
            <h2 className="font-heading text-xl mt-1">Insight AI sul tuo studio</h2>
          </div>
          <Button data-testid="gen-insights-btn" onClick={gen} disabled={loading} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
            <Sparkles size={14} className="mr-1.5"/>{loading ? "Analizzo…" : (insights ? "Rigenera" : "Genera insight")}
          </Button>
        </div>
        {!insights ? (
          <p className="text-zen-ink2 text-sm">Genera un&apos;analisi personalizzata basata su ricavi, marginalità, clienti e fatture scadute.</p>
        ) : (
          <div className="whitespace-pre-wrap text-zen-ink leading-relaxed">{insights.insights}</div>
        )}
      </div>
    </div>
  );
}
